import { useEffect, useId, useState } from "react";
import type { ChangeEvent } from "react";
import { FileSignature, FileText, Lock, Paperclip, Trash2, Upload } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { useToast } from "../../components/Toast";
import { api, fileUrl } from "../../api/client";
import { canWriteQuotes } from "../../api/types";
import type { Attachment, AttachmentKind } from "../../api/types";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { useAuth } from "../auth/useAuth";

// 報價單附件(設計規格 §3)。整個系統第一套檔案上傳的第一個使用者介面;
// 第 13 輪的回簽掃描件、第 15 輪的發票、第 16 輪的雜支收據都會回到這一套。
//
// 四條規矩寫死在這裡:
//
// 1. **上傳走 `api.upload`(FormData),不走 `api.post`。** 那一支送的是 JSON,
//    檔案塞進去只會變成 "[object FormData]"。而且**不可以自己設 Content-Type** ——
//    multipart 的 boundary 由 fetch 產生,手動設了就掉,後端收到一個空的檔案欄位。
// 2. **HEIC 不硬塞進 `<img>`。** 雜支收據多半是拿手機拍的,iPhone 預設就是 HEIC,
//    而瀏覽器多半預覽不出來 —— 硬塞得到的是一張破圖,使用者會以為檔案壞了。
//    看不了的格式退成一個下載連結,並且說出為什麼。
// 3. **唯讀不是看不到。** 已封存的單與財務都看得到附件;差別在傳不了、刪不了。
//    封存是「這個頁面裡的一個動作暫時做不得」,所以按鈕畫出來、反灰、寫明原因;
//    財務則是這件事永遠不是他的工作,整個不畫(設計規格 §2.3),但補一句說明。
// 4. **刪除要先確認。** 刪附件連同磁碟上的位元組一起刪(設計規格 §3.5),
//    救不回來 —— 沒有確認的破壞性動作,遲早有人手滑。
// 5. **上傳回簽要「問」一句要不要改狀態,絕不自動改**(第 13 輪,設計規格 §4)。
//    自動改會讓「我只是先把檔案存起來」這種再正常不過的操作,產生一個沒人預期的
//    副作用 —— 而且那一下會進狀態歷程,看起來像有人真的確認過。選「不用」就
//    真的一發請求都不送。

/**
 * 單檔上限。與後端 `app/storage.py` 的 `MAX_BYTES` 是同一個數字。
 *
 * **前端擋不是為了安全**(繞過去太容易,真正的守門在後端),而是不要讓人
 * 白等一個註定失敗的上傳:一個 50MB 的檔在手機網路上要傳好幾分鐘,
 * 傳完才收到 413,那幾分鐘就是白花的。
 */
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * 檔案選擇器預設只列得出這幾種。
 *
 * 副檔名與 MIME 兩套都寫:有些系統靠副檔名對,有些靠 MIME,而 HEIC 在不少
 * 瀏覽器上 `file.type` 根本是空字串。**這只是選檔時的方便,不是驗證** ——
 * 真正的型別由後端嗅探實際位元組決定(設計規格 §3.2)。
 */
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/jpeg,image/png,image/webp,image/heic";

/** 瀏覽器塞進 `<img>` 就看得到的型別。**HEIC 刻意不在裡面。** */
const PREVIEWABLE = ["image/jpeg", "image/png", "image/webp"];

const PDF_TYPE = "application/pdf";

/**
 * 已封存時的那一句。與後端 `ensure_writable()` 回的 detail、編輯器上那幾顆鈕的
 * 提示都是同一句:按鈕旁邊寫的與真的按下去會收到的錯誤,必須講同一件事。
 */
const ARCHIVED_IS_READ_ONLY = "這張報價單已封存,要編輯請先復原";

/** 財務看得到附件,但上傳與刪除永遠不是他的工作(設計規格 §2.1)。 */
const FINANCE_IS_READ_ONLY = "財務對報價單唯讀,附件只看不改";

/**
 * 位元組換成人看得懂的大小。
 *
 * 不用 `Decimal`:這是檔案大小不是金額,差一個位元組不會有人少收錢,
 * 而且後端送來的本來就是整數位元組。小數固定一位 —— 「1.2 MB」講得夠清楚了,
 * 「1.23 MB」只是在一排檔名旁邊多佔一格寬度。
 */
export function humanSize(bytes: number): string {
  const value = Number.isFinite(bytes) ? Math.max(0, Math.trunc(bytes)) : 0;
  if (value < 1024) return `${value} B`;
  const kb = value / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** 回簽掃描件在清單上的標記。**是文字,不是只有一個圖示或一種顏色。** */
const SIGNED_BACK_MARK = "回簽";

export interface AttachmentPanelProps {
  /** 這些附件掛在哪一張報價單底下。 */
  quoteId: number;
  /** 已封存:附件照樣看得到,但傳不了也刪不了(設計規格 §3.6)。 */
  archived?: boolean;
}

export function AttachmentPanel({
  quoteId,
  archived = false,
}: AttachmentPanelProps) {
  const { user } = useAuth();
  const toast = useToast();
  const inputId = useId();
  const signedBackInputId = useId();
  const headingId = useId();

  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  /** 正在上傳的檔名。`null` 就是沒有在傳 —— 同時只傳一個,畫面才說得清楚。 */
  const [uploading, setUploading] = useState<string | null>(null);
  /**
   * 上傳 / 刪除失敗的那一句。
   *
   * **留在畫面上,不用 toast。** 上傳一個大檔的人多半正在看別的地方,
   * 飄過去三秒就消失的訊息等於沒說過;而且錯誤裡通常帶著下一步該做什麼
   * (「只收得下 PDF、JPEG…」),那是要讀得完的。
   */
  const [error, setError] = useState("");
  /** 等著被確認刪除的那一個。 */
  const [pending, setPending] = useState<Attachment | null>(null);
  const [deleting, setDeleting] = useState(false);
  /**
   * 預覽載不出來的檔案 id。型別白名單擋掉了 HEIC,但圖檔本身壞掉、
   * 或磁碟上的位元組不見了(後端回 410)也一樣會是一張破圖 ——
   * `onError` 是第二道,兩道都到位才真的「不顯示破圖」。
   */
  const [broken, setBroken] = useState<number[]>([]);
  // 財務對報價單唯讀。判斷走 `canWriteQuotes`,不在這裡自己比對角色字串。
  const readOnlyByRole = !canWriteQuotes(user?.role);
  const reason = readOnlyByRole ? FINANCE_IS_READ_ONLY : archived ? ARCHIVED_IS_READ_ONLY : "";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<Attachment[]>(`/quotes/${quoteId}/attachments`)
      .then((rows) => {
        if (!alive) return;
        setItems(Array.isArray(rows) ? rows : []);
        setLoadError("");
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setItems([]);
        setLoadError(err instanceof Error ? err.message : "附件清單載入失敗");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [quoteId]);

  /** 選了檔案。同一個檔連選兩次也要送得出去,所以讀完就把 input 清空。 */
  const pickFile = (event: ChangeEvent<HTMLInputElement>, kind: AttachmentKind) => {
    const file = event.target.files?.[0] ?? null;
    // 不清的話,第二次選同一個檔 `change` 根本不會觸發 ——
    // 使用者只會看到「按了沒反應」,而他做的事完全正確。
    event.target.value = "";
    if (file === null) return;
    void upload(file, kind);
  };

  const upload = async (file: File, kind: AttachmentKind) => {
    setError("");
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(
        `單檔上限 ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB,「${file.name}」有 ${humanSize(file.size)},沒有送出`,
      );
      return;
    }

    setUploading(file.name);
    try {
      // 欄位名 `file` 與後端 `upload_attachment` 的參數名一致。
      const form = new FormData();
      form.append("file", file);
      // `kind` 走 multipart 的表單欄位、與檔案同一個 body —— 後端那一支只吃
      // multipart,JSON body 送不進去。留空是 `GENERAL`,這裡一律明寫。
      form.append("kind", kind);
      const created = await api.upload<Attachment>(`/quotes/${quoteId}/attachments`, form);
      // 後端回的是落地之後的那一列(嗅出來的型別、真正的大小、誰傳的),
      // 拿它接到清單尾巴,不必為了一個檔再撈一次整份清單。
      setItems((prev) => [...prev, created]);
      toast.push(`已上傳「${created.original_name}」`, "success");
    } catch (err: unknown) {
      // **失敗的檔不可以樂觀地先長在清單上。** 使用者會以為傳成功了,
      // 關掉畫面之後才發現什麼都沒有。
      setError(err instanceof Error ? err.message : "上傳失敗");
    } finally {
      setUploading(null);
    }
  };

  const confirmRemove = async () => {
    if (pending === null) return;
    setDeleting(true);
    try {
      await api.del(`/quotes/${quoteId}/attachments/${pending.id}`);
      setItems((prev) => prev.filter((row) => row.id !== pending.id));
      toast.push(`已刪除「${pending.original_name}」`, "success");
      setPending(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "刪除失敗");
      setPending(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-3 shadow-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Paperclip aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
        <h2 id={headingId} className="m-0 text-sm font-semibold text-[var(--text)]">
          附件
        </h2>
        <span className="text-sm text-[var(--text-3)]">{items.length} 個檔案</span>

        {/* 財務整個不畫上傳(這件事永遠不是他的工作);封存中的則畫出來、
            反灰、寫明原因 —— 藏起來只會讓人一直找它跑去哪了。 */}
        {readOnlyByRole ? null : (
          <div className="ml-auto flex items-center gap-2">
            <label
              htmlFor={inputId}
              title={archived ? ARCHIVED_IS_READ_ONLY : undefined}
              className={
                "inline-flex h-9 items-center gap-2 rounded-[var(--radius)] border px-3.5 text-sm font-medium " +
                (archived
                  ? "cursor-not-allowed border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-3)] opacity-55"
                  : "cursor-pointer border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-2)]")
              }
            >
              <Upload aria-hidden="true" size={16} />
              上傳附件
            </label>
            {/* 無障礙名稱就取自上面那個 <label>,**不另外掛 aria-label** ——
                兩邊寫不一樣的字時,語音操作的人唸出畫面上看到的那四個字,
                瀏覽器會找不到這一格(WCAG 2.5.3)。 */}
            <input
              id={inputId}
              type="file"
              accept={ACCEPT}
              disabled={archived || uploading !== null}
              onChange={(event) => pickFile(event, "GENERAL")}
              className="sr-only"
            />

            {/* 回簽是**另一顆鈕**,不是上傳完再挑一次用途(第 13 輪,設計規格 §4)。
                業主的動線是「客戶把簽好的整張掃回來,我們拿它更新這張單」——
                傳的當下就知道那是什麼,問「這是什麼」只是把他已經知道的事再問一次;
                而選錯用途的回簽,在財務那邊是一張永遠找不到的單據。 */}
            <label
              htmlFor={signedBackInputId}
              title={archived ? ARCHIVED_IS_READ_ONLY : undefined}
              className={
                "inline-flex h-9 items-center gap-2 rounded-[var(--radius)] border px-3.5 text-sm font-medium " +
                (archived
                  ? "cursor-not-allowed border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-3)] opacity-55"
                  : "cursor-pointer border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-2)]")
              }
            >
              <FileSignature aria-hidden="true" size={16} />
              上傳回簽
            </label>
            <input
              id={signedBackInputId}
              type="file"
              accept={ACCEPT}
              disabled={archived || uploading !== null}
              onChange={(event) => pickFile(event, "SIGNED_BACK")}
              className="sr-only"
            />
          </div>
        )}
      </div>

      <p className="m-0 text-sm text-[var(--text-3)]">
        收 PDF、JPEG、PNG、WEBP 與 HEIC,單檔上限 20 MB。
      </p>

      {/* 唯讀的原因。狀態不可以只靠反灰(看不到顏色的人什麼都不知道),一律帶文字。 */}
      {reason === "" ? null : (
        <div className="flex items-center gap-1.5">
          <Lock aria-hidden="true" size={14} className="shrink-0 text-[var(--warn)]" />
          <p className="m-0 text-sm text-[var(--text-2)]">{reason}</p>
        </div>
      )}

      {/* 上傳中。十幾 MB 的檔要傳好幾秒,畫面什麼都不說的話使用者會再選一次。 */}
      {uploading === null ? null : (
        <p role="status" className="m-0 text-sm text-[var(--text-2)]">
          上傳中:{uploading}
        </p>
      )}

      {/* 自己按下去才會發生的失敗 → `role="alert"`,螢幕閱讀器當場唸出來。 */}
      {error === "" ? null : (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {error}
        </p>
      )}

      {loading ? (
        <p className="m-0 text-sm text-[var(--text-3)]">讀取附件…</p>
      ) : loadError !== "" ? (
        // 載入失敗是這一塊的狀態,不是一件打斷使用者的事(他什麼都還沒做),
        // 所以寫在清單該在的位置上,不搶焦點也不插話。
        <p className="m-0 text-sm text-[var(--danger)]">附件清單讀不到:{loadError}</p>
      ) : items.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">還沒有附件</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((row) => (
            <AttachmentRow
              key={row.id}
              row={row}
              archived={archived}
              canDelete={!readOnlyByRole}
              broken={broken.includes(row.file_id)}
              onBroken={() => setBroken((prev) => (prev.includes(row.file_id) ? prev : [...prev, row.file_id]))}
              onRemove={() => setPending(row)}
            />
          ))}
        </ul>
      )}

      {/* 要問一次,而且要把檔名寫出來:「確定嗎」問不出使用者到底要刪哪一個。
          第 24 輪起檔案會在回收桶裡放七天才真的清掉(業主 2026-09-20)—— 
          但按錯列仍然是白忙一趟,所以這一句話留著。 */}
      <Dialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title="刪除附件"
        closeOnBackdrop={false}
        footer={
          <>
            <Button onClick={() => setPending(null)}>取消</Button>
            <Button variant="danger" loading={deleting} onClick={() => void confirmRemove()}>
              確定刪除
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-[var(--text)]">
          要刪掉「{pending?.original_name}」嗎?會放進<strong>回收桶七天</strong>,
          期間隨時還原得回來;七天之後檔案本身才真的清掉。
        </p>
      </Dialog>

    </section>
  );
}

interface AttachmentRowProps {
  row: Attachment;
  archived: boolean;
  canDelete: boolean;
  broken: boolean;
  onBroken: () => void;
  onRemove: () => void;
}

/** 清單上的一列:預覽(或替代)、檔名、大小 / 上傳者 / 時間、下載、刪除。 */
function AttachmentRow({ row, archived, canDelete, broken, onBroken, onRemove }: AttachmentRowProps) {
  const href = fileUrl(row.download_url);
  const previewable = PREVIEWABLE.includes(row.content_type) && !broken;
  // 看不了也不是 PDF(PDF 點下去瀏覽器自己開得起來)→ 要說明,不然這一列
  // 看起來只是漏畫了預覽。HEIC 就是走這一條。
  const unviewable = !previewable && row.content_type !== PDF_TYPE;

  return (
    <li className="flex items-center gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] px-2 py-2">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
        {previewable ? (
          // alt 就是原始檔名:看不到圖的人也要知道這是哪一個檔。
          // 載不出來時換成圖示,**不留一張破圖**。
          <img src={href} alt={row.original_name} onError={onBroken} className="h-full w-full object-cover" />
        ) : (
          <FileText aria-hidden="true" size={20} className="text-[var(--text-3)]" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="m-0 flex min-w-0 items-center gap-2 text-sm font-medium text-[var(--text)]">
          <span className="truncate">{row.original_name}</span>
          {/* 回簽與一般附件要分得開:財務對帳時找的就是這一張。
              標記是**文字**,不是只有顏色或圖示(全域規矩)。 */}
          {row.kind === "SIGNED_BACK" ? (
            <Badge tone="accent" className="shrink-0">
              {SIGNED_BACK_MARK}
            </Badge>
          ) : null}
        </p>
        <p className="m-0 text-sm text-[var(--text-3)]">
          {humanSize(row.size_bytes)} · {row.uploaded_by_name || "已離職的同仁"} ·{" "}
          {formatTaipeiDateTime(row.created_at)}
        </p>
        {unviewable ? (
          <p className="m-0 text-sm text-[var(--text-3)]">瀏覽器看不了這個格式,下載後再開</p>
        ) : null}
      </div>

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`下載 ${row.original_name}`}
        className="shrink-0 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1.5 text-sm text-[var(--text)] hover:bg-[var(--surface-2)]"
      >
        下載
      </a>

      {canDelete ? (
        <Button
          size="sm"
          variant="ghost"
          icon={<Trash2 aria-hidden="true" size={16} />}
          aria-label={`刪除附件 ${row.original_name}`}
          // 已封存的單是唯讀的,後端也會回 409 —— 這裡擋的是「按了才知道」。
          disabled={archived}
          title={archived ? ARCHIVED_IS_READ_ONLY : undefined}
          onClick={onRemove}
        >
          刪除
        </Button>
      ) : null}
    </li>
  );
}
